import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MessageCircleIcon, XIcon, ZoomInIcon } from 'lucide-react';
import { ButtonAnchor } from '../ui/Button';
import { testimonios, type Testimonio } from '../../data/testimonios';
import { GENERAL_WA_MESSAGE, whatsappLink } from '../../utils/vehicles';
function TestimonioCard({
  testimonio,
  onZoom,
  duplicated = false
}: {
  testimonio: Testimonio;
  onZoom: (t: Testimonio) => void;
  duplicated?: boolean;
}) {
  return <figure className="w-[210px] shrink-0 overflow-hidden rounded-2xl border border-line bg-surface2/50 sm:w-[230px]" data-id="element-515" data-mp="1y4afow6zh04x">
      <button type="button" onClick={() => onZoom(testimonio)} tabIndex={duplicated ? -1 : undefined} className="group relative block w-full" aria-label={`Ampliar la captura del mensaje de ${testimonio.nombre}`} data-id="element-516" data-mp="1t2l156kjy4c3">
        <img src={testimonio.imagen} alt={duplicated ? '' : `Mensaje de WhatsApp de ${testimonio.nombre} sobre su compra en VCARS`} loading="lazy" className="h-[300px] w-full object-cover object-top sm:h-[330px]" data-id="element-517" data-mp="dmvzquk78u48" />
        <span className="absolute right-2.5 top-2.5 grid h-8 w-8 place-items-center rounded-lg bg-ink/80 text-white opacity-0 backdrop-blur transition-opacity duration-150 ease-smooth group-hover:opacity-100" data-id="element-518" data-mp="3fjmvuk2y8sww">
          <ZoomInIcon className="h-4 w-4" aria-hidden="true" data-id="element-519" data-mp="35u3czef8kz21" />
        </span>
      </button>
      <figcaption className="border-t border-line/70 px-4 py-3" data-id="element-520" data-mp="3amn0b33q78k1">
        <p className="text-[13.5px] font-bold text-white" data-id="element-521" data-mp="2vwyze2zgibwu">{testimonio.nombre}</p>
        <p className="mt-0.5 text-[12px] leading-snug text-mist" data-id="element-522" data-mp="2vuukkq00rshs">
          {testimonio.detalle}
        </p>
      </figcaption>
    </figure>;
}
export function Testimonios() {
  const [zoom, setZoom] = useState<Testimonio | null>(null);
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setZoom(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoom]);
  return <section aria-labelledby="testimonios-titulo" className="border-t border-line/60 bg-ink py-12 md:py-16" data-id="element-523" data-mp="np26wu3vkq9f">
      <div className="mx-auto w-full max-w-7xl px-5 md:px-8" data-id="element-524" data-mp="jo0eox8dlegy">
        <h2 id="testimonios-titulo" className="text-2xl font-extrabold tracking-tight text-white md:text-3xl" data-id="element-525" data-mp="jlnpbz0ag1tv">
          ¡Lo Que Nuestros Clientes Dicen! :)


        </h2>
      </div>

      {/* Carrusel continuo: se detiene al pasar el mouse o al enfocar con teclado. */}
      <div className="vc-marquee-wrap relative mt-6 overflow-hidden" style={{
      maskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)',
      WebkitMaskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)'
    }} data-id="element-526" data-mp="17bi2yisfluf6">
        <div className={`vc-marquee flex w-max gap-4 px-5 md:px-8 ${zoom ? 'vc-marquee-paused' : ''}`} data-id="element-527" data-mp="22m55ulatruqs">
          {testimonios.map(t => <TestimonioCard key={t.id} testimonio={t} onZoom={setZoom} data-id="element-528" data-mp="27ivjl1z5qe91" />)}
          {testimonios.map(t => <TestimonioCard key={`${t.id}-dup`} testimonio={t} onZoom={setZoom} duplicated data-id="element-529" data-mp="2gdbbz08m0qmn" />)}
        </div>

      </div>

      <div className="mx-auto mt-7 flex w-full max-w-7xl flex-wrap items-center gap-x-5 gap-y-3 px-5 md:px-8" data-id="element-530" data-mp="2qbhcpeiojqpx">
        <p className="text-lg font-extrabold tracking-tight text-white md:text-xl" data-id="element-531" data-mp="2vaamowyrczlc">
          +300 ventas exitosas.{' '}
          <span className="text-neon-soft" data-id="element-532" data-mp="2zb4e40beswiz">¿Querés ser el próximo?</span>
        </p>
        <ButtonAnchor href={whatsappLink(GENERAL_WA_MESSAGE)} variant="whatsapp" size="md" data-id="element-533" data-mp="10xkxutn8ygkt">
          <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-534" data-mp="14ywl4sineppu" />
          Hablar con un vendedor
        </ButtonAnchor>
      </div>

      <AnimatePresence data-id="element-535" data-mp="5mmpzuem3h01">
        {zoom ? <motion.div role="dialog" aria-modal="true" aria-label={`Mensaje de ${zoom.nombre}`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={() => setZoom(null)} className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/90 p-5 backdrop-blur-sm" data-id="element-536" data-mp="5lusge1gf974">
            <motion.img initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.96
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} src={zoom.imagen} alt={`Mensaje de WhatsApp de ${zoom.nombre} sobre su compra en VCARS`} onClick={e => e.stopPropagation()} className="max-h-[85vh] w-auto rounded-2xl border border-line object-contain" data-id="element-537" data-mp="o1qxyb3j3d6" />
            <button type="button" onClick={() => setZoom(null)} aria-label="Cerrar" className="absolute right-5 top-5 grid h-11 w-11 place-items-center rounded-xl border border-line bg-surface2/80 text-white" data-id="element-538" data-mp="1jn9axzufhw5k">
              <XIcon className="h-5 w-5" data-id="element-539" data-mp="1xlh2fs8p459c" />
            </button>
          </motion.div> : null}
      </AnimatePresence>
    </section>;
}